// src/App.js
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PermissionRoute from "./components/PermissionRoute";
import LoginPage from "./pages/Login";
import DashboardRouter from "./pages/admin/dashboard/DashboardRouter";
import AdminLayout from "./components/AdminLayout";
import CustomerDashboard from "./pages/users/Dashboard";
import CustomerVehiclesPage from "./pages/users/vehicles/Vehicles";
import AllVehiclesPage from "./pages/users/vehicles/AllVehicles";
import UserLayout from "./components/UserLayout";
import AccountsPage from "./pages/admin/accounts/Accounts";
import AddAccountPage from "./pages/admin/accounts/AddAccount";
import DevicesPage from "./pages/admin/devices/Devices";
import AddDevicePage from "./pages/admin/devices/AddDevice";
import DeviceDetailsPage from "./pages/admin/devices/DeviceDetails";
import AddDeviceTypesPage from "./pages/admin/deviceTypes/AddDeviceTypes";
import DeviceTypesPage from "./pages/admin/deviceTypes/DeviceTypes";
import DeviceAssignmentsPage from "./pages/admin/deviceAssignments/DeviceAssignments";
import AllDeviceAssignmentsPage from "./pages/admin/deviceAssignments/AllDeviceAssignments";
import AddQuotePage from "./pages/admin/quotes/AddQuote";
import QuotesPage from "./pages/admin/quotes/Quotes";
import QuoteDetailsPage from "./pages/admin/quotes/QuoteDetails";
import InvoicePage from "./pages/admin/invoice/invoice";
import AddInvoicePage from "./pages/admin/invoice/AddInvoice";
import AllInvoicesPage from "./pages/admin/invoice/AllInvoices";
import ViewDetailsPage from "./pages/admin/invoice/[id]/ViewDetails";
import ProfilePage from "./pages/admin/Profile";
import ProfileSettingsPage from "./pages/admin/profileSettings/profileSettings";
import MapplsMap from "./pages/admin/map/MapplsMap";
import LiveMap from "./pages/admin/map/LiveMap";
import AllMaps from "./pages/admin/map/AllMaps";
import MapDashboard from "./pages/admin/map/mapDashboard";
import SingleVehicleMap from "./pages/admin/map/SingleVehicleMap";
import SingleVehicleMapHalfUI from "./pages/admin/map/SingleVehicleMapHalfUI";
import PlaybackHistory from "./pages/admin/map/PlaybackHistory";
import LatLngLocationPage from "./pages/admin/map/LatLngLocationPage";
import DayTripsPlaybackHistory from "./pages/admin/map/DayTripsPlaybackHistory";
import VehicleStatsPage from "./pages/admin/map/VehicleStatsPage";
import EngineAccessPage from "./pages/admin/map/EngineAccessPage";
import WalletPage from "./pages/admin/wallet/Wallet";
import RechargePage from "./pages/admin/recharge/recharge";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PERMISSIONS } from "./config/permissions";
import AddCustomerDevices from "./pages/users/customerDevices/AddCustomerDevices";
import AllCustomerDevices from "./pages/users/customerDevices/AllCustomerDevices";
import CustomerDeviceAssignmentDetails from "./pages/users/customerDevices/CustomerDeviceAssignmentDetails";
import SpeedAlertSettings from "./pages/users/SpeedAlertSettings";
import Geofences from "./pages/users/geofences/Geofences";
import GeofenceForm from "./pages/users/geofences/GeofenceForm";
import SubscriptionRouteGate from "./pages/users/SubscriptionRouteGate";
import PaymentRouteGate from "./pages/Navbar/PaymentRouteGate";
import IosPaymentRouteGuard from "./components/IosPaymentRouteGuard";
import IosPaymentRedirect from "./components/IosPaymentRedirect";
import { isRestorableRouteOnPlatform, shouldHideSubscriptionPurchase, isIosBlockedPaymentPath } from "./utils/platform";
import AboutPage from "./pages/Navbar/about";
import HomePage from "./pages/Navbar/Home";
import TermsAndConditionsPage from "./pages/Navbar/termsandConditions";
import PrivacyPolicyPage from "./pages/Navbar/privacyandPolicy";
import AccountDeletionPage from "./pages/Navbar/accountDeletion";
import ReturnAndRefundPage from "./pages/Navbar/returnandRefund";
import GpsTrackerPage from "./pages/Navbar/gpsTracker";
import AnalyticsPage from "./pages/Navbar/Analytics";
import SecurityPage from "./pages/Navbar/security";
import MobileManagementPage from "./pages/Navbar/mobileManagement";
import ContactPage from "./pages/Navbar/contact";
import DeviceWinkPage from "./pages/Navbar/DeviceWink";
import DeviceRevPage from "./pages/Navbar/DeviceRev";
import ReportPage from "./pages/admin/report/report";
import SubscriptionPlansAdminPage from "./pages/admin/subscription/SubscriptionPlansAdmin";
import AlertsPage from "./pages/alerts/AlertsPage";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { useAuth } from "./contexts/AuthContext";

const LAST_VISITED_ROUTE_KEY = "lastVisitedRoute";

const getDefaultRouteForUser = (user) => {
  if (user?.role === "admin" || user?.role === "super_admin" || user?.role === "superadmin") {
    return "/admin/dashboard";
  }
  if (user?.role === "customer" || user?.role === "user") {
    return "/customer/dashboard";
  }
  return "/home";
};

const isAllowedRouteForUser = (route, user) => {
  if (!route || !user) return false;
  if (route.startsWith("/admin")) {
    return user.role === "admin" || user.role === "super_admin" || user.role === "superadmin";
  }
  if (route.startsWith("/customer")) {
    return user.role === "customer" || user.role === "user";
  }
  // Only restore protected app routes for authenticated users.
  return false;
};

const resolveRestorableRoute = (user) => {
  const savedRoute = localStorage.getItem(LAST_VISITED_ROUTE_KEY);
  const blockedRoutes = new Set(["/", "/login", "/unauthorized"]);
  if (
    savedRoute &&
    savedRoute.startsWith("/") &&
    !blockedRoutes.has(savedRoute) &&
    isAllowedRouteForUser(savedRoute, user) &&
    isRestorableRouteOnPlatform(savedRoute, user)
  ) {
    return savedRoute;
  }
  return getDefaultRouteForUser(user);
};

const RoutePersistence = () => {
  const location = useLocation();
  const { loading, isAuthenticated, user } = useAuth();

  useEffect(() => {
    if (!shouldHideSubscriptionPurchase()) return;
    const savedRoute = localStorage.getItem(LAST_VISITED_ROUTE_KEY);
    if (savedRoute && isIosBlockedPaymentPath(savedRoute)) {
      localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
    }
  }, []);

  useEffect(() => {
    if (loading || !isAuthenticated() || !user) return;
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (currentRoute === "/" || currentRoute === "/login" || currentRoute === "/unauthorized") return;
    if (!isAllowedRouteForUser(currentRoute, user)) return;
    if (!isRestorableRouteOnPlatform(currentRoute, user)) return;
    localStorage.setItem(LAST_VISITED_ROUTE_KEY, currentRoute);
  }, [loading, isAuthenticated, user, location.pathname, location.search, location.hash]);

  return null;
};

const RootRedirect = () => {
  const { loading, isAuthenticated, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated() && user) {
    return <Navigate to={resolveRestorableRoute(user)} replace />;
  }

  return <Navigate to="/home" replace />;
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 text-center space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
          <p className="text-muted-foreground">
            You don't have permission to access this page.
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">
            Go Back
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [theme, setTheme] = useState('light');
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    // Get theme from localStorage
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);

    // Check if mobile and if welcome screen has been shown
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobileView(isMobile);
      
      if (isMobile) {
        const hasSeenWelcome = sessionStorage.getItem('welcomeScreenShown');
        if (!hasSeenWelcome) {
          setShowLoadingScreen(true);
          // Mark as shown in this session
          sessionStorage.setItem('welcomeScreenShown', 'true');
          
          // Hide after 3 seconds
          const timer = setTimeout(() => {
            setShowLoadingScreen(false);
          }, 3000);
          
          return () => clearTimeout(timer);
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Global loading screen for mobile
  if (showLoadingScreen) {
    return (
      <>
        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-fade-in {
            animation: fadeIn 1s ease-in-out;
          }
          @keyframes progress {
            0% {
              width: 0%;
            }
            100% {
              width: 100%;
            }
          }
          .animate-progress {
            animation: progress 1.5s ease-in-out infinite;
          }
        `}</style>
        <div className={`min-h-screen flex items-center justify-center relative overflow-hidden transition-colors duration-500 ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e]' 
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
        }`}>
          {/* Welcome Screen for Mobile */}
          <div className="flex flex-col items-center justify-center space-y-8 p-8">
            <div className="animate-fade-in">
              <img 
                src="/logo.png" 
                alt="TrackYourWheelz Logo" 
                className="w-64 h-64 sm:w-80 sm:h-80 object-contain animate-pulse"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
          
          {/* Loading indicator with logo1.png - Progress Bar Style */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-64 sm:w-80">
            <div className="relative h-3 bg-white/20 rounded-full overflow-hidden">
              <div className="absolute h-full left-0 top-0 animate-progress bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-full">
                <img 
                  src="/logo1.png" 
                  alt="Loading" 
                  className="h-full w-auto object-contain opacity-70 absolute right-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
        <RoutePersistence />
        <IosPaymentRouteGuard />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/payment" element={<PaymentRouteGate />} />
          <Route path="/return-refund" element={<ReturnAndRefundPage />} />
          <Route path="/gps-tracker" element={<GpsTrackerPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/mobile-management" element={<MobileManagementPage />} />
          <Route path="/contact-us" element={<ContactPage />} />
          <Route path="/devices/wink" element={<DeviceWinkPage />} />
          <Route path="/devices/rev" element={<DeviceRevPage />} />
          <Route 
            path="/unauthorized" 
            element={<UnauthorizedPage />} 
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin', 'superadmin']}>
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<DashboardRouter />} />
                    <Route path="dashboard" element={<DashboardRouter />} />
                    <Route
                      path="accounts"
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_ACCOUNTS}>
                          <AccountsPage />
                        </PermissionRoute>
                      }
                    />
                    <Route 
                      path="accounts/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_ACCOUNT}>
                          <AddAccountPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route path="devices" element={<DevicesPage />} />
                    <Route 
                      path="devices/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_DEVICE}>
                          <AddDevicePage />
                        </PermissionRoute>
                      } 
                    />
                    <Route path="devices/:deviceId" element={<DeviceDetailsPage />} />
                    <Route path="device-types" element={<DeviceTypesPage />} />
                    <Route 
                      path="device-types/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_DEVICE_TYPE}>
                          <AddDeviceTypesPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="device-assignments/all" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS}>
                          <AllDeviceAssignmentsPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="device-assignments" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_DEVICE_ASSIGNMENT}>
                          <DeviceAssignmentsPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="customer-device-assignments" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS}>
                          <AllCustomerDevices />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="customer-device-assignments/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_DEVICE_ASSIGNMENT}>
                          <AddCustomerDevices />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="customer-device-assignments/:id" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_DEVICE_ASSIGNMENTS}>
                          <CustomerDeviceAssignmentDetails />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="quotes" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_QUOTES}>
                          <QuotesPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="quotes/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_QUOTE}>
                          <AddQuotePage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="quotes/:id" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_QUOTES}>
                          <QuoteDetailsPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="invoices" 
                      element={<AllInvoicesPage />} 
                    />
                    <Route 
                      path="invoices/add" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.ADD_QUOTE}>
                          <AddInvoicePage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="invoices/:id" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_QUOTES}>
                          <InvoicePage />
                        </PermissionRoute>
                      } 
                    />
                    <Route 
                      path="invoices/:id/details" 
                      element={
                        <PermissionRoute requiredPermission={PERMISSIONS.VIEW_QUOTES}>
                          <ViewDetailsPage />
                        </PermissionRoute>
                      } 
                    />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="profile-settings" element={<ProfileSettingsPage />} />
                    <Route path="wallet" element={<WalletPage />} />
                    <Route path="recharge" element={<RechargePage />} />
                    <Route path="alerts" element={<AlertsPage />} />
                    <Route path="map" element={<LiveMap />} />
                    <Route path="map/dashboard" element={<MapDashboard />} />
                    <Route path="map/vehicle/:deviceId" element={<SingleVehicleMap />} />
                    <Route path="map/vehicle-half/:deviceId" element={<SingleVehicleMapHalfUI />} />
                    <Route path="map/view" element={<MapplsMap />} />
                    <Route path="map/playback/:deviceId" element={<PlaybackHistory />} />
                    <Route path="map/day-playback/:deviceId" element={<DayTripsPlaybackHistory />} />
                    <Route path="map/vehicle-stats/:deviceId" element={<VehicleStatsPage />} />
                    <Route path="map/engine-access/:deviceId" element={<EngineAccessPage />} />
                    <Route path="map/location" element={<LatLngLocationPage />} />
                    <Route path="map/trips" element={<AllMaps />} />
                    <Route
                      path="report"
                      element={
                        <ProtectedRoute requiredRoles={['super_admin', 'superadmin']}>
                          <ReportPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="subscription-plans"
                      element={
                        shouldHideSubscriptionPurchase() ? (
                          <IosPaymentRedirect />
                        ) : (
                          <SubscriptionPlansAdminPage />
                        )
                      }
                    />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/*"
            element={
              <ProtectedRoute requiredRoles={['customer', 'user', 'super_admin', 'superadmin']}>
                <UserLayout>
                  <Routes>
                    <Route path="/" element={<CustomerDashboard />} />
                    <Route path="dashboard" element={<CustomerDashboard />} />                
                    <Route path="vehicles" element={<AllVehiclesPage />} />
                    <Route path="Devices" element={<DevicesPage />} />
                    <Route path="vehicles/add" element={<CustomerVehiclesPage />} />                        
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="profile-settings" element={<ProfileSettingsPage />} />
                    <Route path="speed-alerts" element={<SpeedAlertSettings />} />
                    <Route path="geofences" element={<Geofences />} />
                    <Route path="geofences/add" element={<GeofenceForm />} />
                    <Route path="geofences/:geofenceId/edit" element={<GeofenceForm />} />
                    <Route path="alerts" element={<AlertsPage />} />
                    <Route path="map" element={<LiveMap />} />
                    <Route path="map/dashboard" element={<MapDashboard />} />
                    <Route path="map/vehicle/:deviceId" element={<SingleVehicleMap />} />
                    <Route path="map/vehicle-half/:deviceId" element={<SingleVehicleMapHalfUI />} />
                    <Route path="map/view" element={<MapplsMap />} />
                    <Route path="map/playback/:deviceId" element={<PlaybackHistory />} />
                    <Route path="map/day-playback/:deviceId" element={<DayTripsPlaybackHistory />} />
                    <Route path="map/vehicle-stats/:deviceId" element={<VehicleStatsPage />} />
                    <Route path="map/engine-access/:deviceId" element={<EngineAccessPage />} />
                    <Route path="map/location" element={<LatLngLocationPage />} />
                    <Route path="map/trips" element={<AllMaps />} />
                    <Route path="subscription" element={<SubscriptionRouteGate />} />
                  </Routes>
                </UserLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        </Router>
        <ToastContainer
          position={isMobileView ? "top-center" : "top-right"}
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ zIndex: 99999 }}
          toastStyle={isMobileView ? { marginTop: "8px", width: "calc(100vw - 16px)" } : undefined}
        />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
